import { useRef, useState } from "react";
import HCaptcha from "@hcaptcha/react-hcaptcha";
import { IconArrow, IconCheck } from "./Icon";

const WEB3FORMS_ACCESS_KEY = "81a1f0ec-755c-4cc4-8b2b-6643ec75801c";
const HCAPTCHA_SITE_KEY = "50b2fe65-b00b-4b9e-ad62-3ba471098be2";

type Status = "idle" | "submitting" | "success" | "error";

type FormState = {
  email: string;
  phone: string;
  type: "particulier" | "professionnel";
  city: string;
  message: string;
  rgpd: boolean;
  honeypot: string;
};

const initial: FormState = {
  email: "",
  phone: "",
  type: "professionnel",
  city: "",
  message: "",
  rgpd: false,
  honeypot: "",
};

export function ContactForm() {
  const [form, setForm] = useState<FormState>(initial);
  const [status, setStatus] = useState<Status>("idle");
  const [errors, setErrors] = useState<Partial<Record<keyof FormState, string>>>({});
  const [captchaToken, setCaptchaToken] = useState<string>("");
  const [captchaError, setCaptchaError] = useState<string>("");
  const captchaRef = useRef<HCaptcha>(null);

  function update<K extends keyof FormState>(key: K, value: FormState[K]) {
    setForm((f) => ({ ...f, [key]: value }));
    setErrors((e) => ({ ...e, [key]: undefined }));
  }

  function validate(): boolean {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!/^\S+@\S+\.\S+$/.test(form.email)) e.email = "Email invalide.";
    if (form.phone && !/^[0-9 +().-]{6,}$/.test(form.phone))
      e.phone = "Numéro invalide.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Merci de détailler votre besoin (10 caractères min.).";
    if (!form.rgpd) e.rgpd = "Merci d'accepter le traitement de vos données.";
    setErrors(e);
    if (!captchaToken) {
      setCaptchaError("Merci de valider le captcha.");
      return false;
    }
    setCaptchaError("");
    return Object.keys(e).length === 0;
  }

  async function onSubmit(ev: React.FormEvent) {
    ev.preventDefault();
    if (form.honeypot) return;
    if (!validate()) return;
    setStatus("submitting");
    try {
      const payload = {
        access_key: WEB3FORMS_ACCESS_KEY,
        subject: `Nouvelle demande — ${form.type === "professionnel" ? "Pro" : "Particulier"}`,
        from_name: "Site Anexya",
        email: form.email,
        phone: form.phone || "—",
        type: form.type,
        city: form.city || "—",
        message: form.message,
        botcheck: form.honeypot,
        "h-captcha-response": captchaToken,
      };
      const res = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message ?? "Web3Forms error");
      setStatus("success");
      setForm(initial);
      setCaptchaToken("");
      captchaRef.current?.resetCaptcha();
    } catch {
      setStatus("error");
      setCaptchaToken("");
      captchaRef.current?.resetCaptcha();
    }
  }

  if (status === "success") {
    return (
      <div className="rounded-2xl bg-accent-500/10 border border-accent-500/30 p-8 text-center">
        <div className="mx-auto grid h-14 w-14 place-items-center rounded-full bg-accent-500 text-white">
          <IconCheck className="h-7 w-7" />
        </div>
        <h2 className="mt-5 text-2xl">Merci, votre demande a bien été envoyée.</h2>
        <p className="mt-3 text-ink-muted">
          Nous revenons vers vous sous 48 h ouvrées avec un devis personnalisé.
        </p>
        <button
          type="button"
          className="btn-ghost mt-6"
          onClick={() => setStatus("idle")}
        >
          Envoyer une autre demande
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={onSubmit} noValidate className="space-y-5" aria-describedby="form-intro">
      <p id="form-intro" className="sr-only">
        Formulaire de contact - tous les champs marqués d'un astérisque sont obligatoires.
      </p>

      <input
        type="text"
        name="website"
        value={form.honeypot}
        onChange={(e) => update("honeypot", e.target.value)}
        tabIndex={-1}
        autoComplete="off"
        className="hidden"
        aria-hidden="true"
      />

      <fieldset>
        <legend className="label">Vous êtes *</legend>
        <div className="grid grid-cols-2 gap-2" role="radiogroup">
          {(["professionnel", "particulier"] as const).map((v) => (
            <label
              key={v}
              className={`cursor-pointer rounded-xl border px-4 py-3 text-sm font-medium text-center transition ${
                form.type === v
                  ? "border-brand-600 bg-brand-50 text-brand-900"
                  : "border-slate-300 bg-white text-ink-muted hover:border-brand-300"
              }`}
            >
              <input
                type="radio"
                name="type"
                value={v}
                className="sr-only"
                checked={form.type === v}
                onChange={() => update("type", v)}
              />
              {v === "professionnel" ? "Professionnel" : "Particulier"}
            </label>
          ))}
        </div>
      </fieldset>

      <div className="grid sm:grid-cols-2 gap-4">
        <Field id="email" label="Email *" error={errors.email}>
          <input
            id="email"
            type="email"
            required
            autoComplete="email"
            className="input"
            value={form.email}
            onChange={(e) => update("email", e.target.value)}
          />
        </Field>
        <Field id="phone" label="Téléphone" error={errors.phone}>
          <input
            id="phone"
            type="tel"
            autoComplete="tel"
            className="input"
            value={form.phone}
            onChange={(e) => update("phone", e.target.value)}
          />
        </Field>
      </div>

      <Field id="city" label="Commune">
        <input
          id="city"
          type="text"
          autoComplete="address-level2"
          placeholder="Vichy, Cusset, Bellerive…"
          className="input"
          value={form.city}
          onChange={(e) => update("city", e.target.value)}
        />
      </Field>

      <Field id="message" label="Votre message *" error={errors.message}>
        <textarea
          id="message"
          rows={5}
          required
          className="input resize-y"
          placeholder="Décrivez votre besoin, vos horaires souhaités, vos contraintes éventuelles…"
          value={form.message}
          onChange={(e) => update("message", e.target.value)}
        />
      </Field>

      <div>
        <label className="flex items-start gap-3 text-sm text-ink-muted cursor-pointer">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-slate-400 text-brand-600 focus:ring-brand-500"
            checked={form.rgpd}
            onChange={(e) => update("rgpd", e.target.checked)}
          />
          <span>
            J'accepte que mes données soient traitées pour répondre à ma demande, conformément
            à la{" "}
            <a href="/politique-de-confidentialite" className="text-brand-700 underline">
              politique de confidentialité
            </a>
            . *
          </span>
        </label>
        {errors.rgpd && <p className="mt-1 text-sm text-red-600">{errors.rgpd}</p>}
      </div>

      <div>
        <HCaptcha
          ref={captchaRef}
          sitekey={HCAPTCHA_SITE_KEY}
          reCaptchaCompat={false}
          onVerify={(token) => {
            setCaptchaToken(token);
            setCaptchaError("");
          }}
          onExpire={() => setCaptchaToken("")}
          onError={() => setCaptchaToken("")}
        />
        {captchaError && <p className="mt-1 text-sm text-red-600">{captchaError}</p>}
      </div>

      {status === "error" && (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Une erreur est survenue. Merci de réessayer ou de nous appeler.
        </div>
      )}

      <button
        type="submit"
        className="btn-primary w-full sm:w-auto"
        disabled={status === "submitting"}
        aria-disabled={status === "submitting"}
      >
        {status === "submitting" ? "Envoi en cours…" : "Envoyer ma demande"}
        <IconArrow className="h-4 w-4" />
      </button>
    </form>
  );
}

function Field({
  id,
  label,
  error,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <div>
      <label htmlFor={id} className="label">
        {label}
      </label>
      {children}
      {error && (
        <p className="mt-1 text-sm text-red-600" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
